import { Dropdown, Option, Tooltip, makeStyles, tokens } from '@fluentui/react-components';
import { BeakerRegular } from '@fluentui/react-icons';

import { PROTOTYPE_STATE, PROTOTYPE_STATE_OPTIONS, TPrototypeState } from '../../constants';
import { CHANGE_STATUS, TChangeV2 } from '../../models';
import { useChangeV2Store } from '../../state/ChangeV2Store';

const useStyles = makeStyles({
  root: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalXS },
  icon: { color: tokens.colorNeutralForeground3 },
  dropdown: { minWidth: '190px' },
});

// Derives which demo state the current change reflects, so the switcher shows a
// sensible selection. Statuses outside the demo set (e.g. In assessment) map to
// no selection and the dropdown shows its placeholder.
const derivePrototypeState = (
  change: TChangeV2,
  flags: { signedBaseline: boolean; saveFailureArmed: boolean; permissionDenied: boolean }
): TPrototypeState | '' => {
  if (flags.permissionDenied) return PROTOTYPE_STATE.PERMISSION_DENIED;
  if (flags.saveFailureArmed) return PROTOTYPE_STATE.SAVE_FAILED;
  if (flags.signedBaseline) return PROTOTYPE_STATE.READ_ONLY;
  if (change.status === CHANGE_STATUS.CANCELLED) return PROTOTYPE_STATE.CANCELLED;
  if (change.status === CHANGE_STATUS.SUBMITTED) return PROTOTYPE_STATE.SUBMITTED;
  if (change.status === CHANGE_STATUS.DRAFT)
    return change.returnInfo ? PROTOTYPE_STATE.RETURNED : PROTOTYPE_STATE.DRAFT;
  return '';
};

export const PrototypeStateSwitcher = ({ change }: { change: TChangeV2 }) => {
  const classes = useStyles();
  const { signedBaseline, saveFailureArmed, permissionDenied, applyPrototypeState } = useChangeV2Store();
  const current = derivePrototypeState(change, { signedBaseline, saveFailureArmed, permissionDenied });

  return (
    <div className={classes.root}>
      <Tooltip content="Prototype state" relationship="label">
        <BeakerRegular className={classes.icon} />
      </Tooltip>
      <Dropdown
        aria-label="Prototype state"
        className={classes.dropdown}
        placeholder="Live state"
        selectedOptions={current ? [current] : []}
        value={current || 'Live state'}
        onOptionSelect={(_, data) => applyPrototypeState(change.id, data.optionValue as TPrototypeState)}
      >
        {PROTOTYPE_STATE_OPTIONS.map((state) => (
          <Option key={state} value={state} text={state}>
            {state}
          </Option>
        ))}
      </Dropdown>
    </div>
  );
};
